import { observer } from 'mobx-react-lite';
import { selectTaskListAriaText } from './FolderPicker.locstring.json';
import { ActionButton, CommandButton } from '@fluentui/react/lib/Button';
import { DirectionalHint, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Icon, IIconProps } from '@fluentui/react/lib/Icon';
import ChevronDownRegular from 'owa-fluent-icons-svg/lib/icons/ChevronDownRegular';
import loc from 'owa-localize';
import { getDefaultTasksFolderDisplayName } from 'owa-todo-utils';
import * as React from 'react';
import type { TodoListId } from 'owa-todo-list-types';
import { getElementKey } from '../../utils/list/getElementKey';
import type { ListPickerElement } from '../../utils/list/getListPickerElement';
import type { FolderPickerViewConfig } from '../../schema/FolderPickerViewConfig';
import type { IStyle } from '@fluentui/style-utilities';

import styles from './FolderPicker.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export const FolderPicker = observer(function FolderPicker(props: {
    folderPickerElements: ListPickerElement[];
    isSelectedList: (listId: TodoListId) => boolean;
    onSelection: (listId: TodoListId) => void;
    viewConfig?: FolderPickerViewConfig;
    styles?: IStyle;
    onMenuOpened?: () => void;
}) {
    let folderPicker = null;
    const { folderPickerElements, isSelectedList, onSelection, viewConfig, onMenuOpened } = props;
    if (!folderPickerElements || folderPickerElements.length == 0) {
        folderPicker = renderSingleFolder(
            getDefaultTasksFolderDisplayName(),
            viewConfig?.iconLabelProps,
            viewConfig?.styles
        );
    }
    if (folderPickerElements.length == 1) {
        folderPicker = renderSingleFolder(
            folderPickerElements[0].Name,
            viewConfig?.iconLabelProps,
            viewConfig?.styles
        );
    }

    const iconStyle = viewConfig?.styles?.folderPickerCommandButtonIconClass;
    const onRenderMenuIcon = React.useCallback(() => {
        return (
            <Icon
                iconName={ChevronDownRegular}
                className={classNames(styles.commandButtonIcon, iconStyle)}
            />
        );
    }, [iconStyle]);

    folderPicker = renderMultipleFolders(
        folderPickerElements,
        isSelectedList,
        onSelection,
        onRenderMenuIcon,
        viewConfig,
        props.styles,
        onMenuOpened
    );
    return folderPicker;
});

function getIconProps(labelIconProps: IIconProps, iconStyles: string): IIconProps {
    return {
        ...labelIconProps,
        className: iconStyles,
    };
}

/**
 * Renders the text within the command button
 */
function onRenderText(folderName: string, labelStyles: string): () => JSX.Element {
    return () => (
        <div className={labelStyles} title={folderName}>
            {folderName}
        </div>
    );
}

function renderSingleFolder(
    folderName: string,
    labelIconProps: IIconProps,
    customStyles: { folderPickerLabel?: string; folderPickerCommandButtonClass?: string }
): JSX.Element {
    return (
        <CommandButton
            className={classNames(
                styles.multipleFoldersCommandButton,
                customStyles?.folderPickerCommandButtonClass
            )}
            text={folderName}
            onRenderText={onRenderText(
                folderName,
                classNames(styles.commandButtonTextLabel, customStyles?.folderPickerLabel)
            )}
            styles={{
                flexContainer: styles.commandButtonFlexContainer,
                textContainer: styles.commandButtonTextContainer,
            }}
            iconProps={getIconProps(
                labelIconProps,
                classNames(styles.commandButtonTextLabel, labelIconProps?.className)
            )}
        />
    );
}

function renderContextMenuItem(
    folder: ListPickerElement,
    dismissMenu: (
        ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        dismissAll?: boolean
    ) => void,
    isSelectedList: (listId: TodoListId) => boolean,
    onSelection: (listId: TodoListId) => void
): React.ReactNode {
    const onClick = (ev: React.MouseEvent<HTMLElement>) => {
        onSelection(folder.Id);
        dismissMenu(ev);
    };
    const showSelected = isSelectedList(folder.Id);
    return (
        <ActionButton
            key={getElementKey('cm_item', folder.Id)}
            data-is-focusable={true}
            role={'option'}
            className={classNames(styles.menuItemButton, showSelected && styles.showSelected)}
            styles={{
                flexContainer: styles.menuItemButtonFlexContainer,
            }}
            aria-selected={showSelected}
            onClick={onClick}>
            <Icon iconName={folder.IconBefore} className={styles.menuItemIcon} />
            <span className={styles.menuItemText}> {folder.Name} </span>
            {folder.IconAfter && (
                <Icon iconName={folder.IconAfter} className={styles.menuItemIconShared} />
            )}
        </ActionButton>
    );
}

/**
 *  Create the options for the dropdown
 *  @return menu items of type IContextualMenuItem[]
 */
function createMenuItemsForFolders(
    folders: ListPickerElement[],
    isSelectedList: (listId: TodoListId) => boolean,
    onSelection: (listId: TodoListId) => void
) {
    const menuItems: IContextualMenuItem[] = [];
    folders.map(folder => {
        menuItems.push({
            key: getElementKey('cm_item', folder.Id),
            name: folder.Name,
            data: { Id: folder.Id },
            className: styles.menuItemResultText,
            onRender: (
                item: any,
                dismissMenu: (
                    ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
                    dismissAll?: boolean
                ) => void
            ) => {
                return renderContextMenuItem(folder, dismissMenu, isSelectedList, onSelection);
            },
        });
    });
    return menuItems;
}

function renderMultipleFolders(
    folders: ListPickerElement[],
    isSelectedList: (listId: TodoListId) => boolean,
    onSelection: (listId: TodoListId) => void,
    onRenderMenuIcon: () => JSX.Element,
    viewConfig?: FolderPickerViewConfig,
    customStyles?: IStyle,
    onMenuOpened?: () => void
): JSX.Element {
    const selectedFolder = folders.filter(folder => isSelectedList(folder.Id));
    if (selectedFolder.length <= 0) {
        return renderSingleFolder(
            getDefaultTasksFolderDisplayName(),
            viewConfig?.iconLabelProps,
            viewConfig?.styles
        );
    }
    let folderMenuItems: IContextualMenuItem[] = createMenuItemsForFolders(
        folders,
        isSelectedList,
        onSelection
    );
    return (
        <CommandButton
            className={classNames(
                styles.multipleFoldersCommandButton,
                viewConfig?.styles?.folderPickerCommandButtonClass
            )}
            text={selectedFolder[0].Name}
            menuProps={{
                directionalHint: DirectionalHint.bottomLeftEdge,
                items: folderMenuItems,
                isBeakVisible: false,
                ...viewConfig?.menuProps,
                calloutProps: {
                    styles: {
                        calloutMain: styles.taskListFolderCallout,
                    },
                    ...viewConfig?.menuProps?.calloutProps,
                },
                onMenuOpened: onMenuOpened,
            }}
            onWheel={viewConfig?.onWheel}
            onRenderMenuIcon={onRenderMenuIcon}
            ariaLabel={loc(selectTaskListAriaText)}
            onRenderText={onRenderText(
                selectedFolder[0].Name,
                classNames(styles.commandButtonTextLabel, viewConfig?.styles?.folderPickerLabel)
            )}
            styles={{
                flexContainer: classNames(
                    styles.commandButtonFlexContainer,
                    viewConfig?.styles?.folderPickerCommandButtonFlexContainerClass
                ),
                textContainer: customStyles,
                rootExpanded: styles.foldersCommandButtonExpanded,
            }}
            iconProps={getIconProps(
                viewConfig?.iconLabelProps,
                classNames(styles.commandButtonTextLabel, viewConfig?.iconLabelProps?.className)
            )}
        />
    );
}
